<template>
  <div>
    <!-- Quota overview: two separate accounts -->
    <v-row dense class="mb-1">
      <v-col cols="12" sm="6" v-for="acc in accounts" :key="acc.key">
        <v-card outlined>
          <v-card-text class="py-3">
            <div class="d-flex align-center">
              <div class="text-subtitle-2">{{ acc.label }}</div>
              <v-spacer></v-spacer>
              <v-chip v-if="acc.quota && acc.quota.exhausted" small color="error" dark>
                {{ $t('message.egenmelding.exhausted') }}
              </v-chip>
            </div>
            <div v-if="acc.quota" class="text-body-2 mt-1">
              {{ $t('message.egenmelding.remaining') }}:
              <strong>{{ acc.quota.remaining_days }}</strong> / {{ acc.quota.max_days }}
              {{ $t('message.egenmelding.days') }}
              <span v-if="acc.quota.max_instances">
                · {{ acc.quota.used_instances }}/{{ acc.quota.max_instances }}
                {{ $t('message.egenmelding.instances') }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Report form -->
    <v-card outlined>
      <v-card-title class="text-subtitle-1">{{ $t('message.egenmelding.report') }}</v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.type"
                :items="typeItems"
                item-text="text"
                item-value="value"
                :label="$t('message.egenmelding.type')"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu v-model="startMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="$formatter.formatDate(form.start_date, 'YYYY-MM-DD', 'DD.MM.YYYY')"
                    :label="$t('message.egenmelding.startDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    :rules="[v => !!form.start_date || $t('message.common.validationIssue')]"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="form.start_date" first-day-of-week="1" @input="startMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="form.days"
                type="number"
                min="1"
                :max="maxDays"
                :label="$t('message.egenmelding.days')"
                :hint="form.type === 'own' ? $t('message.egenmelding.ownDaysHint') : ''"
                persistent-hint
                :rules="daysRules"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          :loading="submitting"
          :disabled="selectedExhausted"
          @click="submit"
        >{{ $t('message.egenmelding.send') }}</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'FlexEgenmelding',
  data () {
    return {
      valid: false,
      submitting: false,
      startMenu: false,
      form: { type: 'own', start_date: null, days: 1 }
    }
  },
  computed: {
    ...mapGetters('flexAccount', ['getEgenmeldingQuota']),
    quota () {
      return this.getEgenmeldingQuota || { own: null, child: null }
    },
    accounts () {
      return [
        { key: 'own', label: this.$t('message.egenmelding.own'), quota: this.quota.own },
        { key: 'child', label: this.$t('message.egenmelding.child'), quota: this.quota.child }
      ]
    },
    typeItems () {
      return [
        { value: 'own', text: this.$t('message.egenmelding.own') },
        { value: 'child', text: this.$t('message.egenmelding.child') }
      ]
    },
    selectedQuota () {
      return this.quota[this.form.type]
    },
    selectedExhausted () {
      return !!(this.selectedQuota && this.selectedQuota.exhausted)
    },
    maxDays () {
      const remaining = this.selectedQuota ? this.selectedQuota.remaining_days : 0
      // Own sickness caps each instance at 3 calendar days.
      return this.form.type === 'own' ? Math.min(3, remaining) : remaining
    },
    daysRules () {
      return [
        v => (!!v && Number(v) >= 1) || this.$t('message.common.validationIssue'),
        v => Number(v) <= this.maxDays || this.$t('message.egenmelding.tooManyDays')
      ]
    }
  },
  created () {
    this.loadQuota()
  },
  methods: {
    ...mapActions('flexAccount', ['GET_EGENMELDING_QUOTA', 'REPORT_EGENMELDING']),
    loadQuota () {
      this.GET_EGENMELDING_QUOTA()
        .catch(() => this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.errorOccured' }))
    },
    submit () {
      if (!this.$refs.form.validate()) return
      this.submitting = true
      this.REPORT_EGENMELDING({ type: this.form.type, start_date: this.form.start_date, days: Number(this.form.days) })
        .then(() => {
          this.$root.$emit('snackbar', { snackbar: true, color: 'success', text: 'message.egenmelding.reported' })
          this.form = { type: this.form.type, start_date: null, days: 1 }
          this.loadQuota()
        })
        .catch((error) => {
          const code = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors.message : null
          const text = code === 'quota_exceeded' ? 'message.egenmelding.quotaExceeded' : 'message.common.errorOccured'
          this.$root.$emit('snackbar', { snackbar: true, color: 'error', text })
        })
        .finally(() => { this.submitting = false })
    }
  }
}
</script>
